/**
 * Publishing a module.
 *
 * The last step of the builder, and the one that turns a draft into a course: two
 * records, in the order that can be recovered from. The **post** goes first,
 * because a module without a post is a draft and a post without a module is a
 * caption — and the *patch* that puts the module's back-reference on the post comes
 * last, so a failure at any point leaves something a reader can still make sense of
 * (see `#/lib/modulePublish`).
 *
 * The gate is enforced here rather than merely displayed. A module is six published
 * capsules at one subject and one level, and the *dialog* is where that stops being
 * advice: the publish button is disabled until `modulePublishBlockers` is empty,
 * and every blocker is printed as a sentence the creator can act on. The same
 * function decides both, so the button can never be disabled for a reason the
 * screen does not show.
 *
 * Publishing a module *into a classroom* is the same two writes plus the audience
 * question every other kind of work asks (see `#/components/classrooms/AudienceStep`):
 * a room's own course is marked on both records, which is what keeps it out of
 * profiles and feeds while the room plays it.
 */
import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {AtUri} from '@atproto/api'
import {Plural, Trans, useLingui} from '@lingui/react/macro'
import {useQueryClient} from '@tanstack/react-query'

import {
  moduleAudience,
  modulePostFacts,
  modulePostRecord,
  modulePublishTarget,
} from '#/lib/modulePublish'
import {
  MODULE_COLLECTION,
  type ModuleBlocker,
  type ModuleItem,
} from '#/lib/modulePlaylist'
import {moduleRecordOf} from '#/lib/moduleSave'
import {
  createActorModulesQueryKey,
  createModuleByUriQueryKey,
} from '#/state/queries/module'
import {useAgent} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ClassroomAudienceStep} from '#/components/classrooms/AudienceStep'
import {moduleBlockerSentence} from '#/components/modules/ModuleBlockerText'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import {Check_Stroke2_Corner0_Rounded as CheckThin} from '#/components/icons/Check'
import {PaperPlane_Stroke2_Corner0_Rounded as PaperPlane} from '#/components/icons/PaperPlane'
import {Loader} from '#/components/Loader'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'

export interface PublishedModule {
  /** The module record. */
  moduleUri: string
  moduleCid: string
  /** The post it was published as. */
  postUri: string
  postCid: string
  /** Whether it was published for one classroom. */
  forClassroom?: boolean
}

export function PublishModuleDialog({
  control,
  moduleUri,
  createdAt,
  title,
  description,
  level,
  subject,
  items,
  coins,
  blockers,
  classroomName,
  onSuccess,
}: {
  control: DialogControlProps
  /** The draft's own address: publishing converts that record in place. */
  moduleUri: string | undefined
  /** The draft's creation date, so publishing does not backdate it. */
  createdAt: string
  title: string
  description: string
  level?: string | null
  subject?: string | null
  items: readonly ModuleItem[]
  coins: number
  /** What is stopping this module; empty means it publishes. */
  blockers: readonly ModuleBlocker[]
  /** The classroom this studio was opened from, when it was opened from one. */
  classroomName?: string
  onSuccess?: (published: PublishedModule) => void
}) {
  return (
    <Dialog.Outer control={control} testID="publishModuleDialog">
      <Dialog.Handle />
      <Inner
        control={control}
        moduleUri={moduleUri}
        createdAt={createdAt}
        title={title}
        description={description}
        level={level}
        subject={subject}
        items={items}
        coins={coins}
        blockers={blockers}
        classroomName={classroomName}
        onSuccess={onSuccess}
      />
    </Dialog.Outer>
  )
}

function Inner({
  control,
  moduleUri,
  createdAt,
  title,
  description,
  level,
  subject,
  items,
  coins,
  blockers,
  classroomName,
  onSuccess,
}: {
  control: DialogControlProps
  moduleUri: string | undefined
  createdAt: string
  title: string
  description: string
  level?: string | null
  subject?: string | null
  items: readonly ModuleItem[]
  coins: number
  blockers: readonly ModuleBlocker[]
  classroomName?: string
  onSuccess?: (published: PublishedModule) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const agent = useAgent()
  const queryClient = useQueryClient()
  const [audience, setAudience] = useState<'public' | 'classroom'>('public')
  const [isPending, setPending] = useState(false)
  const [isSuccess, setSuccess] = useState(false)

  const forClassroom = !!classroomName && audience === 'classroom'
  const ready = blockers.length === 0

  const onPublish = useCallback(async () => {
    if (!ready) return
    setPending(true)
    try {
      const now = new Date().toISOString()
      const did = agent.session!.did
      const marks = moduleAudience(forClassroom)

      /* 1. The post. It says what the module is before anything else exists. */
      const postRecord = modulePostRecord({
        text: title + (description ? '\n\n' + description : ''),
        createdAt: now,
        visibility: marks.postVisibility,
      })
      const postRes = await agent.com.atproto.repo.createRecord({
        repo: did,
        collection: 'app.bsky.feed.post',
        record: postRecord,
      })

      /* 2. The module record, converted from the draft in place. */
      const record = moduleRecordOf({
        title,
        description,
        status: 'published',
        items,
        level,
        subject,
        postUri: postRes.data.uri,
        postCid: postRes.data.cid,
        visibility: marks.recordVisibility,
        createdAt: createdAt || now,
        updatedAt: now,
      })
      const target = modulePublishTarget(moduleUri)
      const moduleRes =
        target.mode === 'update'
          ? await agent.com.atproto.repo.putRecord({
              repo: did,
              collection: MODULE_COLLECTION,
              rkey: target.rkey,
              record,
            })
          : await agent.com.atproto.repo.createRecord({
              repo: did,
              collection: MODULE_COLLECTION,
              record,
            })

      /* 3. The patch: the module's back-reference on the post that carries it. */
      const postRkey = new AtUri(postRes.data.uri).rkey
      const patched = await agent.com.atproto.repo.putRecord({
        repo: did,
        collection: 'app.bsky.feed.post',
        rkey: postRkey,
        record: {
          ...postRecord,
          ...modulePostFacts({
            moduleUri: moduleRes.data.uri,
            moduleCid: moduleRes.data.cid ?? '',
            itemCount: items.length,
            coins,
            level,
            subject,
          }),
        },
      })

      /*
       * The author's own list of modules changed, and this is often the page the
       * studio was opened from — so without this a creator comes back to a grid
       * that does not contain what they just published.
       */
      void queryClient.invalidateQueries({
        queryKey: createActorModulesQueryKey(did),
      })
      void queryClient.invalidateQueries({
        queryKey: createModuleByUriQueryKey(moduleRes.data.uri),
      })

      setSuccess(true)
      Toast.show(
        forClassroom
          ? l`Module published into the classroom!`
          : l`Module published!`,
      )
      setTimeout(() => {
        control.close()
        onSuccess?.({
          moduleUri: moduleRes.data.uri,
          moduleCid: moduleRes.data.cid ?? '',
          postUri: patched.data.uri,
          postCid: patched.data.cid ?? '',
          ...(forClassroom ? {forClassroom: true} : null),
        })
      }, 1500)
    } catch (e: unknown) {
      console.error('Failed to publish module', e)
      const message = e instanceof Error ? e.message : undefined
      Toast.show(message || l`Failed to publish module`)
      setPending(false)
    }
  }, [
    agent,
    coins,
    classroomName,
    control,
    createdAt,
    description,
    forClassroom,
    items,
    level,
    moduleUri,
    onSuccess,
    queryClient,
    ready,
    subject,
    title,
    l,
  ])

  return (
    <Dialog.ScrollableInner
      testID="publishModuleDialogInner"
      label={l`Publish module`}
      style={[a.w_full, {maxWidth: 500}]}>
      <View style={[a.gap_lg, a.pt_md]}>
        <View style={[a.gap_xs]}>
          <Text style={[a.text_xl, a.font_bold]}>
            <Trans>Publish this module</Trans>
          </Text>
          {/*
            Two plurals, one after the other, rather than one message with two
            numbers in it.

            Each is a phrase a translator can pluralise on its own terms, and
            neither has to reach for an escaped space to join itself to the other —
            a `#` followed by a quoted literal is not something an ICU plural's
            forms survive, and the first version of this line took the whole dialog
            down with it when it opened.
          */}
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            <Plural value={items.length} one="One capsule" other="# capsules" />
            {', '}
            <Plural value={coins} one="# coin to win" other="# coins to win" />
          </Text>
        </View>

        {/*
          What is stopping it, in the order a creator fixes it.
          
          Every line is a blocker the publish button is disabled by, printed as an
          instruction rather than as a code: the two cannot disagree, because both
          read the same list.
        */}
        {ready ? (
          <View
            testID="publishModuleReady"
            style={[a.p_md, a.rounded_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, t.atoms.text]}>
              <Trans>
                Every capsule in this module is published, and they all belong
                to the same subject and level. It is ready.
              </Trans>
            </Text>
          </View>
        ) : (
          <View
            testID="publishModuleBlockers"
            style={[a.p_md, a.rounded_md, a.gap_xs, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_sm, a.font_bold, t.atoms.text]}>
              <Trans>Before it can be published</Trans>
            </Text>
            {blockers.map(blocker => (
              <Text
                key={`${blocker.code}-${'uri' in blocker ? blocker.uri : ''}`}
                style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {moduleBlockerSentence(blocker, l)}
              </Text>
            ))}
          </View>
        )}

        {classroomName ? (
          <ClassroomAudienceStep
            testIDPrefix="moduleAudience"
            question={l`Who can play it?`}
            publicTitle={l`Anyone — publish it to your profile`}
            publicDescription={l`It goes on your profile and into the capsule feed, where anyone can find it and play it.`}
            classroomTitle={l`Only ${classroomName}`}
            classroomDescription={l`It belongs to this classroom. It stays out of your profile and out of every feed — its members play it, and the board it keeps is theirs.`}
            note={l`A module's capsules are ordinary capsules: they are unaffected by where the module itself is published.`}
            forClassroom={forClassroom}
            onChange={wantsClassroom =>
              setAudience(wantsClassroom ? 'classroom' : 'public')
            }
          />
        ) : null}

        <Text style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
          <Trans>
            Publishing puts the module on your profile and lets anyone play it
            as one run. Its capsules keep their own pages, and the board it
            keeps is public to everyone who plays it.
          </Trans>
        </Text>

        <Button
          testID="publishModuleBtn"
          label={l`Publish module`}
          size="large"
          variant="solid"
          color="primary"
          disabled={isPending || isSuccess || !ready}
          onPress={onPublish}>
          <ButtonText>
            {forClassroom ? l`Publish into the classroom` : l`Publish module`}
          </ButtonText>
          <ButtonIcon
            icon={isSuccess ? CheckThin : isPending ? Loader : PaperPlane}
          />
        </Button>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}
